import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { CustomerType } from '../model/customer';
import { Advertisment, CommunicationMechanism, LoginResponse, Menu, OnlineReservationCategory, RestaurantsList } from '../model/login-response.dto';
import { Reservation } from '../model/reservation';
import { Common } from './common-methods';
import { UtilsService } from './utils.service';
import { BatchSynchRequestUpdated } from '@model/tables-plan';
const sha256 = require('js-sha256');

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  constructor(private translate: TranslateService) {
    this.deviceId = null;
    this.refreshInterval = null;
    this.serverToken = null;
    this.restaurantId = null;
    this.workingDate = null;
    this.lang = null;
    this.customerTypes = null;
    this.compactListView = null;
    try {
      const data = localStorage.getItem("userData");
      this.userData = data == null ? null : JSON.parse(data);
    } catch (e) {
      console.log("failed to parse user data", e);
      this.userData = null;
    }
  }

  private deviceId: string | null;
  private refreshInterval: number | null;
  private serverToken: string | null;
  private restaurantId: string | null;
  private userData: LoginResponse | null;
  private workingDate: string | null;
  private lang: string | null;
  private customerTypes: CustomerType[] | null;
  private compactListView: boolean | null;

  isAuthenticated() {
    const t = localStorage.getItem("serverToken");
    return t != null && t != "" && t.length > 0 && this.userData != null;
  }

  private getPlatform(): 'web' | 'ios' | 'android' {
    return Capacitor.getPlatform() as any;
  }

  setRefreshInterval(restaurants: RestaurantsList[]) {
    if (restaurants == null || restaurants.length == 0) return;

    let interval = this.getPlatform() == 'web' ?
      restaurants[0].pocketWebSchedulerRunIntervalSeconds :
      restaurants[0].pocketAppSchedulerRunIntervalSeconds;

    if (interval == null || interval < 0 || isNaN(interval)) {
      interval = 30;
    }

    this.refreshInterval = interval;
    localStorage.setItem("refreshInterval", interval + '');
  }

  getRefreshInterval(): number {
    if (!this.refreshInterval) {
      // fetch existing
      this.refreshInterval = parseInt(localStorage.getItem("refreshInterval") ?? '30');
    }

    if (this.refreshInterval == null || this.refreshInterval < 0 || isNaN(this.refreshInterval)) {
      this.refreshInterval = 30;
    }

    return this.refreshInterval * 1000;
  }

  clear() {
    this.serverToken = null;
    this.restaurantId = null;
    this.userData = null;
    localStorage.removeItem("serverToken");
    localStorage.removeItem("restaurantId");
    localStorage.removeItem("userData");
    localStorage.removeItem("workingDate");
    localStorage.removeItem("menus");
    localStorage.removeItem("customerTypes");
  }

  getRandomString(length: number) {
    return Math.round((Math.pow(36, length + 1) - Math.random() * Math.pow(36, length))).toString(36).slice(1);
  }

  getDeviceId() {
    if (!this.deviceId) {
      // fetch existing
      this.deviceId = localStorage.getItem("deviceId");
    }

    // ask device for one
    if (!this.deviceId) {

      // use a random 32 chars string
      if (!this.deviceId) {

        this.deviceId = this.getRandomString(32);
      }

      // save it for future use
      this.saveDeviceId(sha256(this.deviceId));
    }

    return this.deviceId;
  }

  saveDeviceId(deviceId: string) {
    this.deviceId = deviceId;
    return localStorage.setItem("deviceId", deviceId);
  }

  saveServerToken(token: string) {
    this.serverToken = token;
    return localStorage.setItem("serverToken", token);
  }

  saveUserData(res: LoginResponse) {
    this.userData = res;
    localStorage.setItem("userData", JSON.stringify(res));
  }

  getUserData() {
    return this.userData;
  }

  getServerToken() {
    //if (!this.serverToken) {
    this.serverToken = localStorage.getItem("serverToken");
    //}
    return this.serverToken;
  }

  saveCustomerTypes(customerTypes: CustomerType[] | null) {
    if (customerTypes == undefined) {
      customerTypes = null;
    }

    this.initCustomerTypes(customerTypes);
    this.customerTypes = customerTypes;

    return localStorage.setItem("customerTypes", JSON.stringify(customerTypes));
  }

  getCustomerTypes(): CustomerType[] {

    if (this.customerTypes != null && this.customerTypes.length > 0) {
      return this.customerTypes;
    }

    let customerTypes: CustomerType[] = [];
    try {
      customerTypes = JSON.parse(localStorage.getItem("customerTypes") + "");
    } catch (e) {

    }

    if (customerTypes == null || customerTypes.length == 0) {
      customerTypes = [
        { order: '1', description_el: "Απλός", description_en: "Simple", value: "S", ireserve: false, color: Common.getCustomerTypeColor('S'), description: '' },
        { order: '2', description_el: "Θαμώνας", description_en: "Regular", value: "R", ireserve: false, color: Common.getCustomerTypeColor('R'), description: '' },
        { order: '3', description_el: "VIP", description_en: "VIP", value: "VIP", ireserve: false, color: Common.getCustomerTypeColor('VIP'), description: '' },
        { order: '4', description_el: "Θαμώνας VIP", description_en: "Regular VIP", value: "R&VIP", ireserve: false, color: Common.getCustomerTypeColor('R&VIP'), description: '' },
        { order: '5', description_el: "Super VIP", description_en: "Super VIP", value: "SVIP", ireserve: false, color: Common.getCustomerTypeColor('SVIP'), description: '' },
        { order: '6', description_el: "Μαύρη Λίστα", description_en: "Black List", value: "BL", ireserve: false, color: Common.getCustomerTypeColor('BL'), description: '' },
      ];
    }

    this.initCustomerTypes(customerTypes);
    this.customerTypes = customerTypes;

    return this.customerTypes;
  }

  initCustomerTypes(customerTypes: CustomerType[] | null) {

    if (customerTypes == null) {
      return;
    }

    const lang = this.translate.currentLang;

    for (let customerType of customerTypes) {

      if (lang === 'el') {
        customerType.description = customerType.description_el;
      } else if (lang === 'hr') {
        customerType.description = customerType.description_hr ?? customerType.description_en;
      } else if (lang === 'sr') {
        customerType.description = customerType.description_sr ?? customerType.description_en;
      } else {
        customerType.description = customerType.description_en;
      }

      if (customerType.color == null) {
        customerType.color = Common.getCustomerTypeColor(customerType.value) ?? "#3f51b5";
      }
    }

    customerTypes.sort(function (a, b) {
      return (a.order > b.order)
        ? 1
        : ((b.order > a.order) ? -1 : 0);
    });
  }

  saveRestaurantId(restaurantId: string) {
    this.restaurantId = restaurantId;
    return localStorage.setItem("restaurantId", restaurantId);
  }

  getRestaurantId() {
    if (!this.restaurantId) {
      this.restaurantId = localStorage.getItem("restaurantId");
    }
    return this.restaurantId;
  }

  saveWorkingDate(reservationsListDate: string) {
    this.workingDate = reservationsListDate;
    return localStorage.setItem("workingDate", reservationsListDate);
  }

  getWorkingDate() {
    if (!this.workingDate) {
      this.workingDate = localStorage.getItem("workingDate");
    }
    return this.workingDate;
  }

  saveMenus(restaurants: RestaurantsList[]) {
    if (restaurants == null || restaurants.length == 0) return;

    var menus = restaurants[0].menus;

    if (menus == undefined) {
      menus = null;
    }

    return localStorage.setItem("menus", JSON.stringify(menus));
  }

  getMenus() {
    var menus: Menu[] = JSON.parse(localStorage.getItem("menus") ?? '[]');

    if (menus == null || menus.length == 0) return null;

    const lang = this.getLanguage();
    for (let menu of menus) {

      if (lang === 'el') {
        menu.description = menu.description_el;
      } else if (lang === 'hr') {
        menu.description = menu.description_hr ?? menu.description_en;
      } else if (lang === 'sr') {
        menu.description = menu.description_sr ?? menu.description_en;
      } else {
        menu.description = menu.description_en;
      }

    }

    menus.sort((a, b) => {
      return (a.order > b.order)
        ? 1
        : ((b.order > a.order) ? -1 : 0);
    });

    return menus;
  }

  areMenusAvailable() {
    return this.getMenus() != null;
  }

  private availableLanguages = ["el", "en", "hr", "sr", "de", "ru"];

  getLanguage() {
    if (this.lang == null) {
      this.lang = localStorage.getItem("lang");
    }

    const lang = this.lang ?? "el";

    return this.availableLanguages.indexOf(lang) == -1 ? "en" : lang;
  }

  setLanguage(lang: string) {
    this.lang = lang;
    this.translate.use(lang);
    return localStorage.setItem("lang", lang);
  }

  getPendingReservations(): Reservation[] {
    const stored = localStorage.getItem("pendingReservations");

    if (stored == null) {
      return [];
    }

    return JSON.parse(stored);
  }

  setPendingReservations(pendingReservations: Reservation[]) {
    return localStorage.setItem("pendingReservations", JSON.stringify(pendingReservations));
  }

  getPendingTablePlans(): BatchSynchRequestUpdated[] {
    const stored = localStorage.getItem("pendingTablePlans");

    if (stored == null) {
      return [];
    }

    return JSON.parse(stored);
  }

  setPendingTablePlans(pendingTablePlans: BatchSynchRequestUpdated[]) {
    return localStorage.setItem("pendingTablePlans", JSON.stringify(pendingTablePlans));
  }

  saveMinTelephoneLength(restaurants: RestaurantsList[]) {
    if (restaurants == null || restaurants.length == 0) return;

    var value = restaurants[0].telLengthValidator;

    return localStorage.setItem("minTelephoneLength", value);
  }

  getMinTelephoneLength() {
    var value = localStorage.getItem("minTelephoneLength");

    if (value == null || value == "null" || isNaN(parseInt(value)) === true) {
      return 10;
    }
    else {
      return parseInt(value);
    }
  }

  saveOnlineCategories(restaurants: RestaurantsList[]) {
    if (restaurants == null || restaurants.length == 0) return;

    let onlineCategories: OnlineReservationCategory[] | null = restaurants[0].onlineReservationCategories;

    if (onlineCategories == undefined) {
      onlineCategories = null;
    }

    return localStorage.setItem("onlineCategories", JSON.stringify(onlineCategories));
  }

  getOnlineCategories() {
    var onlineCategories = JSON.parse(localStorage.getItem("onlineCategories") ?? '[]');

    if (onlineCategories == null) {
      onlineCategories = [];
    }

    return onlineCategories;
  }

  getOnlineCategoriesHiddenExcluded(): OnlineReservationCategory[] {

    const onlineCategoryList = JSON.parse(localStorage.getItem("onlineCategories") ?? '[]');
    const empty = { name: "\u2014" };
    let onlineCategories = [];
    onlineCategories.push(empty);

    onlineCategoryList.sort(function (a: any, b: any) {
      return (a.displayOrder > b.displayOrder)
        ? 1
        : ((b.displayOrder > a.displayOrder) ? -1 : 0);
    });

    for (let category of onlineCategoryList) {
      if (category.hidden) continue;

      let iconCaption = category.iconCaption;

      if (iconCaption.length > 4)
        iconCaption = iconCaption.substring(iconCaption.length - 4);

      category.iconCaption = iconCaption;

      const l = this.getLanguage();

      let name = category.name

      if (this.isJsonString(name)) {
        name = JSON.parse(category.name);

        if (l === "el")
          name = name.el;
        else
          name = name.en;
      }

      //HEADER
      if (category.subCategories == null || category.subCategories.length == 0) {
        var item = { id: category.id, photo: category.photo, name: category.name, description: category.description, terms: category.terms, menuLink: category.menuLink, hidden: category.hidden, displayOrder: category.displayOrder, timeperformed: category.timeperformed, iconColor: category.iconColor, iconCaption: category.iconCaption, subCategories: category.subCategories };

        item.name = name;

        onlineCategories.push(item);
        continue;
      }

      onlineCategories = this.findSubCategories(category, onlineCategories, name);
    }

    return onlineCategories;
  }

  isJsonString(jsonValue: string) {
    try {
      JSON.parse(jsonValue);
    } catch (e) {
      return false;
    }

    return true;
  }

  findSubCategories(category: OnlineReservationCategory, onlineCategories: OnlineReservationCategory[], name: string) {

    var counter = 0;
    var nameForThisLevel = "";

    (category.subCategories ?? []).sort(function (a: any, b: any) {
      return (a.displayOrder > b.displayOrder)
        ? 1
        : ((b.displayOrder > a.displayOrder) ? -1 : 0);
    });

    var hasAtLeastOneUnhiddenChild = false;

    let sub;
    for (sub of (category.subCategories ?? [])) {
      if (sub.hidden) continue;
      //SUB CATEGORY
      hasAtLeastOneUnhiddenChild = true;
      counter++;

      if (counter == 1) {
        nameForThisLevel = name;
      } else if (counter > 1) name = nameForThisLevel;

      let subName = sub.name ?? '';

      var l = this.getLanguage();

      if (l == "gr")
        subName = subName.substring(subName.indexOf('el') + 6, subName.indexOf('en') - 4);
      else
        subName = subName.substring(subName.indexOf('en') + 6, subName.indexOf('}', subName.indexOf('en')) - 1);

      name += "\u2192" + subName;

      if (sub.subCategories == null || sub.subCategories.length == 0) {
        if (sub.hidden) continue;
        var item = { id: sub.id, photo: sub.photo, name: sub.name, description: sub.description, terms: sub.terms, menuLink: sub.menuLink, hidden: sub.hidden, displayOrder: sub.displayOrder, timeperformed: sub.timeperformed, iconColor: sub.iconColor, iconCaption: sub.iconCaption, subCategories: sub.subCategories };

        item.name = name;
        onlineCategories.push(item);
        continue;
      }
      onlineCategories = this.findSubCategories(sub, onlineCategories, name);
    }

    if (!hasAtLeastOneUnhiddenChild) {
      const item = { id: sub?.id, photo: sub?.photo, name: sub?.name, description: sub?.description, terms: sub?.terms, menuLink: sub?.menuLink, hidden: sub?.hidden, displayOrder: sub?.displayOrder, timeperformed: sub?.timeperformed, iconColor: sub?.iconColor, iconCaption: sub?.iconCaption, subCategories: sub?.subCategories };

      item.name = name;
      onlineCategories.push(item);
    }

    return onlineCategories;
  }

  saveHasOnlineReservations(restaurants: RestaurantsList[]) {
    if (restaurants == null || restaurants.length == 0) return;

    let hasOnlineReservations: boolean | null = restaurants[0].hasOnlineReservations;

    if (hasOnlineReservations == undefined) {
      hasOnlineReservations = null;
    }

    return localStorage.setItem("hasOnlineReservations", JSON.stringify(hasOnlineReservations));

  }

  getHasOnlineReservations() {
    var hasOnlineReservations = JSON.parse(localStorage.getItem("hasOnlineReservations") ?? 'null');

    if (hasOnlineReservations == null) {
      hasOnlineReservations = [];
    }

    return hasOnlineReservations;
  }

  saveExtraStatuses(restaurants: RestaurantsList[]) {
    if (restaurants == null || restaurants.length == 0) return;

    let statusInfo = restaurants[0].optionalStatuses;

    if (statusInfo == null) {
      statusInfo = {};
    }

    var statuses = [];

    if (statusInfo.Dessert == true) {
      statuses.push("Dessert");
    }

    if (statusInfo.Payment == true) {
      statuses.push("Payment");
    }

    if (statusInfo.Prepare == true) {
      statuses.push("Prepare");
    }

    return localStorage.setItem("extraStatuses", JSON.stringify(statuses));
  }

  getExtraStatuses() {
    var stored = localStorage.getItem("extraStatuses");

    return stored == null
      ? []
      : JSON.parse(stored);
  }

  saveCommunication(restaurants: RestaurantsList[]) {
    if (restaurants == null || restaurants.length == 0) return;

    var com = restaurants[0].communicationMechanism;

    if (com == undefined || restaurants[0].hasCommunicationMechanism != true) {
      com = null;
    }

    return localStorage.setItem("communication", JSON.stringify(com));
  }

  getCommunication(): CommunicationMechanism | null {
    var com: CommunicationMechanism = JSON.parse(localStorage.getItem("communication") ?? '[]');

    if (com == null) return null;

    return com;
  }

  saveServiceStopDate(restaurants: RestaurantsList[]) {
    try {
      if (restaurants == null || restaurants.length == 0) return;

      var dateFormatted: string | null = restaurants[0].thirdPartyFinalPaymentDate;

      if (dateFormatted == null) dateFormatted = "";

      localStorage.setItem("serviceStopDate", dateFormatted);
    }
    catch {
      localStorage.removeItem("serviceStopDate");
    }
  }

  getServiceStopDate(): Date | null {
    try {
      var dtString = localStorage.getItem("serviceStopDate");

      if (dtString == null || dtString == "") return null;

      return moment(dtString, "YYYY-MM-DD").startOf("day").toDate();
    }
    catch {
      return null;
    }
  }
  
  saveCompactListView(value: boolean) {
    this.compactListView = value;
    localStorage.setItem("compactListView", `${value}`);
  }

  getCompactListView() {
    if (this.compactListView == null) {
      this.compactListView = localStorage.getItem("compactListView") !== 'false';
    }
    return this.compactListView;
  }  
}
